// Client Authentication System
// Simple, secure authentication for client portal

// Client database (in production, this would be server-side)
const CLIENTS = {
  'fairfield': {
    password: 'fairfield#2025',
    name: 'Fairfield Auto Parts',
    proposals: ['fairfield-redesign-2025'],
    email: 'contact@fairfieldautoparts.com.au'
  }
  // Add more clients here as needed
};

// Simple hash function for password comparison (in production, use proper hashing)
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

// Authentication class
class ClientAuth {
  constructor() {
    this.sessionKey = 'codex_client_session';
    this.inactivityTimeout = 30 * 60 * 1000; // 30 minutes
    this.inactivityTimer = null;
    this.setupInactivityTracking();
  }

  // Check if user is logged in
  isAuthenticated() {
    const session = this.getSession();
    if (!session) return false;

    // Check if session is expired
    const now = Date.now();
    if (now > session.expiresAt) {
      this.logout();
      return false;
    }

    return true;
  }

  // Get current session
  getSession() {
    const sessionData = sessionStorage.getItem(this.sessionKey);
    if (!sessionData) return null;

    try {
      return JSON.parse(sessionData);
    } catch (e) {
      return null;
    }
  }

  // Get current client data
  getCurrentClient() {
    const session = this.getSession();
    if (!session) return null;
    return CLIENTS[session.username];
  }

  // Login method
  login(username, password) {
    const client = CLIENTS[username.toLowerCase()];

    if (!client) {
      return { success: false, message: 'Invalid username or password' };
    }

    if (client.password !== password) {
      return { success: false, message: 'Invalid username or password' };
    }

    // Create session
    const session = {
      username: username.toLowerCase(),
      loginTime: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
      lastActivity: Date.now()
    };

    sessionStorage.setItem(this.sessionKey, JSON.stringify(session));
    this.resetInactivityTimer();

    return {
      success: true,
      message: 'Login successful',
      client: client
    };
  }

  // Logout method
  logout() {
    sessionStorage.removeItem(this.sessionKey);
    this.clearInactivityTimer();
    window.location.href = '/client-login.html';
  }

  // Update last activity time
  updateActivity() {
    const session = this.getSession();
    if (session) {
      session.lastActivity = Date.now();
      sessionStorage.setItem(this.sessionKey, JSON.stringify(session));
      this.resetInactivityTimer();
    }
  }

  // Setup inactivity tracking
  setupInactivityTracking() {
    // Track user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, () => {
        if (this.isAuthenticated()) {
          this.updateActivity();
        }
      }, true);
    });

    this.resetInactivityTimer();
  }

  // Reset inactivity timer
  resetInactivityTimer() {
    this.clearInactivityTimer();

    this.inactivityTimer = setTimeout(() => {
      if (this.isAuthenticated()) {
        alert('Your session has expired due to inactivity. Please log in again.');
        this.logout();
      }
    }, this.inactivityTimeout);
  }

  // Clear inactivity timer
  clearInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  // Protect a page (call this on protected pages)
  protectPage() {
    if (!this.isAuthenticated()) {
      window.location.href = '/client-login.html';
      return false;
    }
    return true;
  }

  // Check if client has access to a specific proposal
  hasAccess(proposalId) {
    const client = this.getCurrentClient();
    if (!client) return false;
    return client.proposals.includes(proposalId);
  }
}

// Create global auth instance
const auth = new ClientAuth();

// Export for use in other modules
export { auth, ClientAuth };
